import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    Users,
    UserPlus,
    DollarSign,
    CheckCircle2,
    XCircle,
    CreditCard,
    ChevronLeft,
    ArrowUpRight,
    TrendingUp,
    Clock
} from 'lucide-react';

const AdminRrpp = () => {
    const { slug } = useParams();
    const [requests, setRequests] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            const [reqs, comms] = await Promise.all([
                axios.get(`/${slug}/api/rrpp/requests`),
                axios.get(`/${slug}/api/rrpp/commissions`)
            ]);
            setRequests(reqs.data);
            setCommissions(comms.data);
        } catch (err) {
            console.error('Error fetching RRPP data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [slug]);

    const handleApprove = async (id, status) => {
        try {
            const commissionRate = status === 'approved' ? 0.05 : 0;
            await axios.put(`/${slug}/api/rrpp/approve/${id}`, { status, commissionRate });
            setMessage({ text: `Solicitud ${status === 'approved' ? 'aprobada' : 'rechazada'}.`, type: 'success' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setMessage({ text: 'Error al procesar la solicitud.', type: 'error' });
        }
    };

    const handlePayout = async (rrppId) => {
        if (!window.confirm('¿Confirmas que ya le pagaste a este RRPP por fuera del sistema?')) return;
        try {
            await axios.put(`/${slug}/api/rrpp/payout/${rrppId}`);
            setMessage({ text: 'Comisiones marcadas como pagadas.', type: 'success' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setMessage({ text: 'Error al procesar el pago.', type: 'error' });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
            </div>
        );
    }

    const pendingRequests = requests.filter(r => r.status === 'pending');

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20">
            {/* HERO / HEADER */}
            <div className="relative pt-12 pb-20 px-6 border-b border-white/5 bg-white/[0.01]">
                <div className="mx-auto max-w-7xl">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-4">
                                <Users size={12} className="text-primary-orange" />
                                <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60 text-center">Panel Administrativo</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">Gestión de <span className="text-gradient">RRPP</span></h1>
                            <p className="text-white/40 font-medium">Aprobá solicitudes, controlá ventas y gestioná pagos de comisiones.</p>
                        </div>

                        <div className="flex gap-4">
                            <StatMini label="Total RRPP" value={commissions.length} icon={<Users size={16} />} color="orange" />
                            <StatMini label="Pendientes" value={pendingRequests.length} icon={<Clock size={16} />} color="blue" />
                        </div>
                    </div>

                    {message && (
                        <div className={`animate-fade-in-up p-4 rounded-2xl flex items-center gap-3 border ${message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
                            }`}>
                            {message.type === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                            <span className="font-bold text-sm tracking-tight">{message.text}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-6 -mt-10 relative z-10 space-y-12">

                {/* SOLICITUDES PENDIENTES */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                            <UserPlus size={20} className="text-blue-400" />
                        </div>
                        <h2 className="text-xl font-black tracking-tight uppercase tracking-[2px]">Solicitudes <span className="text-blue-400">Pendientes</span></h2>
                    </div>

                    {pendingRequests.length === 0 ? (
                        <div className="bg-glass p-12 rounded-[32px] text-center border-dashed">
                            <p className="text-white/20 font-bold uppercase tracking-widest text-xs">No hay solicitudes pendientes de aprobación</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {pendingRequests.map(req => (
                                <div key={req._id} className="bg-glass p-6 rounded-3xl group hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center font-black text-white/40 border border-white/5 group-hover:bg-primary-orange/10 group-hover:text-primary-orange transition-colors">
                                            {req.userId.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-black text-lg leading-tight mb-1">{req.userId.name}</p>
                                            <p className="text-sm text-white/40 font-medium">{req.userId.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => handleApprove(req._id, 'approved')}
                                            className="px-6 py-3 bg-green-500 text-white rounded-2xl font-black text-sm hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                                        >
                                            <CheckCircle2 size={18} /> APROBAR
                                        </button>
                                        <button
                                            onClick={() => handleApprove(req._id, 'rejected')}
                                            className="px-6 py-3 bg-white/5 text-white/40 hover:text-red-400 border border-white/5 hover:border-red-400/20 rounded-2xl font-black text-sm transition-all"
                                        >
                                            RECHAZAR
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* VENTAS Y COMISIONES */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                            <TrendingUp size={20} className="text-primary-orange" />
                        </div>
                        <h2 className="text-xl font-black tracking-tight uppercase tracking-[2px]">Ventas y <span className="text-primary-orange">Comisiones</span></h2>
                    </div>

                    {commissions.length === 0 ? (
                        <div className="bg-glass p-12 rounded-[32px] text-center border-dashed">
                            <p className="text-white/20 font-bold uppercase tracking-widest text-xs">Aún no hay actividad de RRPPs registrada</p>
                        </div>
                    ) : (
                        <div className="bg-glass rounded-[40px] overflow-hidden border border-white/5 shadow-3xl">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-white/5">
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40">RRPP</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40">Ventas</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40">Comisión Total</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40">Pendiente</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40">Datos Bancarios</th>
                                            <th className="px-8 py-5 text-[10px] font-black uppercase tracking-[3px] text-white/40 text-right">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {commissions.map(c => (
                                            <tr key={c.rrppId} className="group hover:bg-white/[0.02] transition-colors">
                                                <td className="px-8 py-6">
                                                    <p className="font-bold text-white mb-1">{c.name}</p>
                                                    <p className="text-xs text-white/30 font-medium">{c.email}</p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm font-black">
                                                        {c.totalSales}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <p className="text-lg font-black text-green-400">${c.totalCommission.toLocaleString()}</p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <p className={`text-lg font-black ${c.unpaidCommission > 0 ? 'text-red-400' : 'text-white/20'}`}>
                                                        ${c.unpaidCommission.toLocaleString()}
                                                    </p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    {c.paymentDetails && (c.paymentDetails.cbu || c.paymentDetails.alias) ? (
                                                        <div className="space-y-1 bg-white/5 p-3 rounded-2xl border border-white/5 group-hover:border-white/10 transition-all">
                                                            {c.paymentDetails.alias && <p className="text-xs font-bold text-white/60">Alias: <span className="text-white select-all">{c.paymentDetails.alias}</span></p>}
                                                            {c.paymentDetails.cbu && <p className="text-[10px] font-bold text-white/40">CBU: <span className="text-white/60 select-all font-mono">{c.paymentDetails.cbu}</span></p>}
                                                            {c.paymentDetails.titular && <p className="text-[10px] font-black uppercase tracking-widest text-primary-orange/50">{c.paymentDetails.titular}</p>}
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/10 italic">Sin datos</span>
                                                    )}
                                                </td>
                                                <td className="px-8 py-6 text-right">
                                                    {c.unpaidCommission > 0 ? (
                                                        <button
                                                            onClick={() => handlePayout(c.rrppId)}
                                                            className="px-5 py-2.5 bg-brand-gradient text-white rounded-xl font-black text-xs hover:scale-105 active:scale-95 transition-all shadow-lg shadow-orange-500/20"
                                                        >
                                                            PAGAR
                                                        </button>
                                                    ) : (
                                                        <span className="flex items-center justify-end gap-1 text-[10px] font-black uppercase text-white/20">
                                                            <CheckCircle2 size={12} /> AL DÍA
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

const StatMini = ({ label, value, icon, color }) => {
    const colors = {
        orange: 'text-primary-orange bg-primary-orange/10 border-primary-orange/20',
        blue: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    };
    return (
        <div className={`p-4 rounded-[24px] border border-white/5 bg-white/[0.02] flex items-center gap-4 min-w-[160px]`}>
            <div className={`p-2 rounded-xl border ${colors[color]}`}>
                {icon}
            </div>
            <div>
                <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px] mb-0.5">{label}</p>
                <p className="text-2xl font-black leading-none">{value}</p>
            </div>
        </div>
    );
}

export default AdminRrpp;
